import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { AuthService } from '@core/services/auth.service';

/**
 * Adds the current tenant/company context to Ninja-Agent-X API requests.
 *
 * Path A (Option A2): request-scoped tenant header used for server-side enforcement.
 *
 * Header name is kept as `X-ScanNinja-CompanyId` for compatibility with existing docs,
 * but the expected value is the canonical scanner tenant UUID.
 */
@Injectable()
export class NinjaAgentTenantInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private platformId = inject(PLATFORM_ID);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const baseUrl = environment.ninja_agent_api_url;

    // Only attach to Ninja-Agent-X API calls.
    if (!baseUrl || !req.url.startsWith(baseUrl)) {
      return next.handle(req);
    }

    // Allow callers to override explicitly.
    if (req.headers.has('X-ScanNinja-CompanyId')) {
      return next.handle(req);
    }

    // SSR: avoid depending on browser-only auth state.
    if (!isPlatformBrowser(this.platformId)) {
      return next.handle(req);
    }

    const companyId = this.authService.getCurrentUserActivecurrentCompanyId();
    if (!companyId) {
      return next.handle(req);
    }

    return next.handle(
      req.clone({
        setHeaders: {
          'X-ScanNinja-CompanyId': companyId,
        },
      })
    );
  }
}
